/**
 * /vaccines/v/<slug> — the flagship single-vaccine page.
 *
 * Structured per the "Page Contract":
 *   Q1 What is it?         → hero + Evidence Overview panel
 *   Q2 What connected to?  → manufacturer / ingredient / disease / surveillance + RelationshipGraph
 *   Q3 What evidence?      → grouped EvidenceBlocks by evidence_label
 *   Q4 Where next?         → ContinueExploring with linked entities + investigations
 *
 * SEO: canonical, JSON-LD Drug + MedicalEntity, all internal links
 * crawlable, "last reviewed" date when present.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Syringe, ExternalLink, Calendar, FlaskConical, Building2, Activity, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import ResearchPageScaffold from "../../components/research/ResearchPageScaffold";
import EvidenceOverviewPanel from "../../components/research/EvidenceOverviewPanel";
import EvidenceBlock from "../../components/research/EvidenceBlock";
import RelationshipGraph from "../../components/research/RelationshipGraph";
import ContinueExploring from "../../components/research/ContinueExploring";
import SaveButton from "../../components/research/SaveButton";
import CiteAndPrint from "../../components/research/CiteAndPrint";
import RegulatoryDocumentsPanel from "../../components/research/RegulatoryDocumentsPanel";
import HistoricalTimelinePanel from "../../components/research/HistoricalTimelinePanel";
import ReviewStamp from "../../components/research/ReviewStamp";

const EVIDENCE_SECTIONS = [
    { label: "official_guidance", anchor: "official", title: "Official guidance", subtitle: "From NHS, CDC and other health authorities." },
    { label: "regulatory_document", anchor: "regulatory", title: "Regulatory documents", subtitle: "From MHRA, FDA, EMA — SmPCs, PILs, approval records." },
    { label: "peer_reviewed", anchor: "research", title: "Published research", subtitle: "Peer-reviewed studies. Chips show study type and evidence grade." },
    { label: "safety_surveillance", anchor: "surveillance", title: "Safety surveillance", subtitle: "Yellow Card, VAERS, EudraVigilance. Detect signals — not proof of causation." },
    { label: "historical", anchor: "history", title: "Historical documentation", subtitle: "Schedule changes, decisions and reasoning over time." },
    { label: "tcp_investigation", anchor: "context", title: "TCP investigations", subtitle: "Connecting this vaccine to the wider archive." },
];

function ConnectedEntitiesPanel({ vaccine, entities }) {
    const byType = useMemo(() => {
        const out = { manufacturer: [], ingredient: [], disease: [] };
        for (const id of vaccine.manufacturer_entity_ids || []) {
            const e = entities.find((x) => x.id === id);
            if (e) out.manufacturer.push(e);
        }
        for (const id of vaccine.ingredient_entity_ids || []) {
            const e = entities.find((x) => x.id === id);
            if (e) out.ingredient.push(e);
        }
        for (const id of vaccine.disease_target_entity_ids || []) {
            const e = entities.find((x) => x.id === id);
            if (e) out.disease.push(e);
        }
        return out;
    }, [vaccine, entities]);

    const cards = [
        { title: "Manufacturer", icon: Building2, items: byType.manufacturer, empty: "Not yet documented." },
        { title: "Ingredients", icon: FlaskConical, items: byType.ingredient, empty: "Ingredient list pending publication." },
        { title: "Diseases targeted", icon: Activity, items: byType.disease, empty: "Not yet documented." },
    ];

    return (
        <section id="connected" className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cards.map((c) => (
                <div key={c.title} className="bg-[#111827] border border-[#243044] rounded-xl p-5" data-testid={`vaccine-connected-${c.title.toLowerCase()}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <c.icon className="w-4 h-4 text-[#56CCF2]" />
                        <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] font-semibold">{c.title}</h3>
                    </div>
                    {c.items.length === 0 ? (
                        <p className="text-[12px] text-[#64748B] italic">{c.empty}</p>
                    ) : (
                        <ul className="space-y-1.5">
                            {c.items.map((e) => (
                                <li key={e.id}>
                                    <Link to={`/entity/${e.slug}`} className="text-sm text-white hover:text-[#56CCF2] transition-colors">
                                        {e.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}
        </section>
    );
}

function SchedulesPanel({ schedules, vaccine, parentRef }) {
    const byCountry = useMemo(() => {
        const out = { UK: [], US: [] };
        for (const s of schedules) (out[s.country] = out[s.country] || []).push(s);
        // Sort each country's doses by age, then dose_number.
        for (const c of Object.keys(out)) {
            out[c].sort((a, b) => (a.age_months - b.age_months) || (a.dose_number - b.dose_number));
        }
        return out;
    }, [schedules]);

    // Build aligned-by-dose-number rows so timing differences are scannable.
    const maxDoses = Math.max(
        ...["UK", "US"].map((c) => byCountry[c].length ? byCountry[c][byCountry[c].length - 1].total_doses : 0),
        0,
    );
    const alignedRows = useMemo(() => {
        if (!maxDoses) return [];
        const rows = [];
        for (let dose = 1; dose <= maxDoses; dose++) {
            const ukRow = byCountry.UK.find((r) => r.dose_number === dose);
            const usRow = byCountry.US.find((r) => r.dose_number === dose);
            rows.push({ dose, uk: ukRow || null, us: usRow || null });
        }
        return rows;
    }, [byCountry, maxDoses]);

    const summary = (country) => {
        const rows = byCountry[country];
        if (!rows || rows.length === 0) return null;
        const total = Math.max(...rows.map((r) => r.total_doses));
        const yr = Math.max(...rows.map((r) => r.schedule_year || 0));
        const sourceUrl = rows.find((r) => r.source_url)?.source_url;
        return { total, count: rows.length, year: yr, sourceUrl };
    };
    const ukS = summary("UK");
    const usS = summary("US");

    const onSchedule = ukS || usS;
    if (!onSchedule) {
        return (
            <section id="schedule" className="scroll-mt-24 bg-[#111827] border border-[#243044] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-[#56CCF2]" />
                    <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] font-semibold">Schedule</h3>
                </div>
                <p className="text-[12px] text-[#64748B]">Not currently on the UK or US routine childhood schedule.</p>
            </section>
        );
    }

    return (
        <section id="schedule" className="scroll-mt-24 bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid="vaccine-schedule-comparison">
            <div className="flex items-center justify-between px-5 py-3 bg-[#0B1220] border-b border-[#243044]">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#56CCF2]" />
                    <h3 className="text-sm font-semibold text-white tracking-tight">UK vs US schedule</h3>
                    <span className="text-[10px] text-[#64748B] ml-1">Compare timing, dose count and official sources</span>
                </div>
                <SaveButton
                    kind="vaccine_schedule"
                    refSlug={`schedule-${vaccine.slug}`}
                    refTitle={`${vaccine.display_name} — UK vs US schedule`}
                    parentRef={parentRef}
                    fragment="#schedule"
                    refUrl={`/vaccines/v/${vaccine.slug}#schedule`}
                />
            </div>

            {/* Country summary cards */}
            <div className="grid grid-cols-2 divide-x divide-[#243044]" data-testid="vaccine-schedule-summary">
                {[
                    { c: "UK", s: ukS, label: "United Kingdom", reg: "NHS / MHRA" },
                    { c: "US", s: usS, label: "United States", reg: "CDC / FDA" },
                ].map(({ c, s, label, reg }) => (
                    <div key={c} className="p-4">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-1">{label}</div>
                        <div className="text-[10px] text-[#64748B] mb-3">{reg}</div>
                        {s ? (
                            <div className="space-y-1.5">
                                <div className="text-sm text-white"><span className="text-2xl font-bold">{s.total}</span> <span className="text-[12px] text-[#94A3B8]">dose{s.total === 1 ? "" : "s"} total</span></div>
                                {s.year ? <div className="text-[11px] text-[#64748B]">Schedule {s.year}</div> : null}
                                {s.sourceUrl ? (
                                    <a href={s.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#56CCF2] hover:text-white transition-colors">
                                        <ExternalLink className="w-3 h-3" />
                                        Official source
                                    </a>
                                ) : null}
                            </div>
                        ) : (
                            <div className="text-[12px] text-[#475569] italic">Not on the routine schedule.</div>
                        )}
                    </div>
                ))}
            </div>

            {/* Dose-by-dose alignment */}
            {alignedRows.length > 0 ? (
                <div className="border-t border-[#243044]">
                    <div className="grid grid-cols-[80px_1fr_1fr] text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] bg-[#0B1220] border-b border-[#243044]">
                        <div className="px-4 py-2.5 font-semibold">Dose</div>
                        <div className="px-4 py-2.5 font-semibold border-l border-[#243044]">UK timing</div>
                        <div className="px-4 py-2.5 font-semibold border-l border-[#243044]">US timing</div>
                    </div>
                    {alignedRows.map((row) => {
                        // Highlight rows where timing differs.
                        const differs = row.uk && row.us && row.uk.age_months !== row.us.age_months;
                        const onlyOne = (row.uk && !row.us) || (!row.uk && row.us);
                        return (
                            <div
                                key={row.dose}
                                className={`grid grid-cols-[80px_1fr_1fr] text-sm border-t border-[#243044] ${differs || onlyOne ? "bg-[#1a1410]" : ""}`}
                                data-testid={`schedule-dose-${row.dose}`}
                            >
                                <div className="px-4 py-3 font-mono text-[#56CCF2]">{row.dose}</div>
                                <div className="px-4 py-3 border-l border-[#243044] text-[13px]">
                                    {row.uk ? (
                                        <span className="text-white">{row.uk.age_label}</span>
                                    ) : (
                                        <span className="text-[#475569] italic">Not scheduled</span>
                                    )}
                                </div>
                                <div className="px-4 py-3 border-l border-[#243044] text-[13px]">
                                    {row.us ? (
                                        <span className="text-white">{row.us.age_label}</span>
                                    ) : (
                                        <span className="text-[#475569] italic">Not scheduled</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {alignedRows.some((r) => (r.uk && r.us && r.uk.age_months !== r.us.age_months) || (r.uk && !r.us) || (!r.uk && r.us)) ? (
                        <div className="px-4 py-2 bg-[#0B1220] border-t border-[#243044] text-[11px] text-[#94A3B8]">
                            <span className="inline-block w-2 h-2 rounded-sm bg-[#3a2b1a] mr-2 align-middle"></span>
                            Highlighted rows differ between countries — timing, dose count or eligibility.
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* Per-country notes if any */}
            {schedules.some((s) => s.notes_md) ? (
                <div className="border-t border-[#243044] p-4 space-y-2">
                    {schedules.filter((s) => s.notes_md).map((s) => (
                        <div key={s.id} className="text-[12px]">
                            <span className="text-[#56CCF2] uppercase tracking-wider mr-2">{s.country}</span>
                            <span className="text-[#94A3B8]">{s.age_label}:</span>{" "}
                            <span className="text-[#CBD5E1]">{s.notes_md}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </section>
    );
}

export default function VaccinePage() {
    const { slug } = useParams();
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/vaccines/${slug}/detail`)
            .then(({ data }) => setBundle(data))
            .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
            .finally(() => setLoading(false));
    }, [slug]);

    // Hooks must be called in the same order on every render — keep all of
    // them above the loading / notFound early returns. We use safe defaults
    // when bundle is null so the dependency arrays remain stable.
    const evidence_blocks = bundle?.evidence_blocks || [];
    const entities = bundle?.entities || [];
    const vaccine = bundle?.vaccine || {};
    const grouped = useMemo(() => {
        const out = {};
        for (const b of evidence_blocks) (out[b.evidence_label] = out[b.evidence_label] || []).push(b);
        return out;
    }, [evidence_blocks]);

    const continueItems = useMemo(() => {
        const items = [];
        for (const e of entities) {
            if ((vaccine.manufacturer_entity_ids || []).includes(e.id)
                || (vaccine.ingredient_entity_ids || []).includes(e.id)
                || (vaccine.disease_target_entity_ids || []).includes(e.id)) {
                items.push({ kind: e.entity_type, slug: e.slug, title: e.name, summary: e.summary_md?.slice(0, 140), badge: e.entity_type.replace("_", " ") });
            }
        }
        return items;
    }, [entities, vaccine]);

    if (loading) return <div className="min-h-screen bg-[#0B1220] text-[#94A3B8] flex items-center justify-center">Loading…</div>;
    if (notFound || !bundle) return <Navigate to="/vaccines" replace />;

    const { schedules, evidence_counts, relationships } = bundle;
    const entitiesById = Object.fromEntries(entities.map((e) => [e.id, e]));
    const parentRef = `vaccine:${vaccine.slug}`;
    const canonicalPath = `/vaccines/v/${vaccine.slug}`;
    const description = vaccine.description_md
        ? vaccine.description_md.replace(/\n+/g, " ").trim()
        : `Research record for ${vaccine.display_name} — official UK and US schedule, evidence, and connected investigations.`;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Drug",
        name: vaccine.display_name,
        alternateName: vaccine.aka || [],
        description,
        url: `https://library.thecontextproject.com${canonicalPath}`,
    };

    return (
        <ResearchPageScaffold
            title={vaccine.display_name}
            description={description}
            canonicalPath={canonicalPath}
            jsonLd={jsonLd}
            eyebrow={vaccine.aka && vaccine.aka.length ? `Also known as ${vaccine.aka.join(" · ")}` : "Vaccine"}
            breadcrumbs={[
                { label: "Library", href: "/" },
                { label: "Vaccines", href: "/vaccines" },
                { label: vaccine.display_name },
            ]}
            bookmark={{ kind: "vaccine", refSlug: vaccine.slug, refTitle: vaccine.display_name }}
            reviewStamp={
                vaccine.last_reviewed_at ? (
                    <ReviewStamp
                        lastReviewedAt={vaccine.last_reviewed_at}
                        nextReviewDueAt={vaccine.next_review_due_at}
                    />
                ) : null
            }
            heroSlot={
                <div className="space-y-6">
                    <EvidenceOverviewPanel counts={evidence_counts} />
                    <CiteAndPrint title={vaccine.display_name} canonicalPath={canonicalPath} slug={vaccine.slug} />
                </div>
            }
            connectedSlot={
                <div className="space-y-6">
                    <SchedulesPanel schedules={schedules} vaccine={vaccine} parentRef={parentRef} />
                    <RegulatoryDocumentsPanel documents={vaccine.regulatory_documents || []} />
                    <HistoricalTimelinePanel timeline={vaccine.historical_timeline || []} />
                    <ConnectedEntitiesPanel vaccine={vaccine} entities={entities} />
                    {relationships.length > 0 ? (
                        <RelationshipGraph
                            focusEntity={null}
                            relationships={relationships}
                            entitiesById={entitiesById}
                            parentRef={parentRef}
                        />
                    ) : null}
                </div>
            }
            evidenceSlot={
                <div className="space-y-10" id="evidence-section">
                    <header>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-1.5">Safety &amp; Evidence</div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">What evidence exists for {vaccine.display_name}?</h2>
                        <p className="text-[14px] text-[#94A3B8] mt-2 max-w-3xl">
                            We organise information by the type of evidence it comes from so you can see what each source can and cannot tell you. Official guidance is not the same as a controlled trial. A surveillance signal is not the same as a causal finding.
                        </p>
                    </header>
                    {EVIDENCE_SECTIONS.map((sec) => {
                        const items = grouped[sec.label] || [];
                        if (items.length === 0) return null;
                        return (
                            <div key={sec.label} id={`evidence-${sec.anchor}`} className="scroll-mt-24">
                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold text-white tracking-tight">{sec.title}</h3>
                                    <p className="text-[12px] text-[#64748B] mt-1">{sec.subtitle}</p>
                                </div>
                                <div className="space-y-4">
                                    {items.map((b) => (
                                        <EvidenceBlock key={b.id} block={b} parentRef={parentRef} vaccineSlug={vaccine.slug} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {evidence_blocks.length === 0 ? (
                        <div className="bg-[#111827] border border-[#243044] rounded-xl p-6 text-[13px] text-[#64748B]" data-testid="evidence-empty">
                            <AlertTriangle className="w-4 h-4 inline mr-2 text-[#E0B341]" />
                            Evidence sections are being authored. Check back soon.
                        </div>
                    ) : null}
                </div>
            }
            continueSlot={
                continueItems.length > 0 ? <ContinueExploring items={continueItems} parentRef={parentRef} /> : null
            }
        />
    );
}
